import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Share2, User, MapPin, Clock, Tag, Sparkles, MessageCircle, Flag, Ban, MoreHorizontal, Twitter, Send as SendIcon, Copy } from 'lucide-react';
import { timeAgo, isNewListing } from '../utils/timeAgo';
import { API } from '../App';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ReportDialog } from './ReportDialog';

const tg = () => (typeof window !== 'undefined' ? window.Telegram?.WebApp : null);
const haptic = (style = 'light') => {
  try { tg()?.HapticFeedback?.impactOccurred(style); } catch { /* ignore */ }
};

const PRETTY_CATEGORY = {
  nft: 'nfts_digital',
  physical: 'arts_collectibles',
  xrp: 'xrp_pool',
};

/**
 * Compact action row for marketplace-style listings.
 * Renders: Share, Profile, Chat (if telegram), Overflow menu (Report, Block).
 *
 * Props:
 *   shareUrl, shareTitle, hostUsername, hostTelegramUsername, hostUserId,
 *   targetType ('hosted_game'|'auction'|'user'), targetId, targetName,
 *   isOwnListing (bool — hides report/block/chat), onBlocked (callback)
 */
export function ListingActions({
  shareUrl,
  shareTitle = 'Check out this listing on Vitamin C Gainz',
  hostUsername,
  hostTelegramUsername,
  hostUserId,
  targetType,
  targetId,
  targetName,
  isOwnListing = false,
  onBlocked,
  className = '',
}) {
  const navigate = useNavigate();
  const [showReport, setShowReport] = useState(false);

  /**
   * Build the public sharable URL. Priority:
   *  - explicit `shareUrl` prop (caller knows best)
   *  - backend `/api/share/game/{id}` (OG-tagged HTML for social crawlers; auto-redirects humans to Telegram app)
   *  - backend `/api/share/auction/{id}` (auctions — TODO)
   *  - window.location.href (fallback)
   */
  const publicShareUrl = (() => {
    if (shareUrl) return shareUrl;
    if (typeof window !== 'undefined' && targetId && targetType === 'hosted_game') {
      const backendOrigin = (API || '').replace(/\/api\/?$/, '');
      if (backendOrigin) return `${backendOrigin}/api/share/game/${targetId}`;
    }
    if (typeof window !== 'undefined' && targetId && targetType === 'auction') {
      const backendOrigin = (API || '').replace(/\/api\/?$/, '');
      if (backendOrigin) return `${backendOrigin}/api/share/auction/${targetId}`;
    }
    return typeof window !== 'undefined' ? window.location.href : '';
  })();

  const handleShareTo = async (channel) => {
    haptic('medium');
    const text = shareTitle;
    const url = publicShareUrl;
    if (channel === 'twitter') {
      const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
      window.open(tweet, '_blank', 'noopener,noreferrer');
      return;
    }
    if (channel === 'telegram') {
      const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
      const tgApp = tg();
      if (tgApp?.openTelegramLink) {
        try { tgApp.openTelegramLink(tgUrl); return; } catch { /* fall through */ }
      }
      window.open(tgUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (channel === 'copy') {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied!');
      } catch {
        toast.error('Unable to copy');
      }
      return;
    }
    // native share sheet (mobile)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text, url });
        return;
      } catch { /* user cancelled — fall through */ }
    }
    // ultimate fallback
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success('Link copied!');
    } catch {
      toast.error('Unable to share');
    }
  };

  // Kept for back-compat with any older callers (currently unused — Share is a dropdown).
  const handleShare = () => handleShareTo('native');
  void handleShare;

  const handleProfile = () => {
    if (!hostUsername) return;
    haptic('light');
    navigate(`/profile/${encodeURIComponent(hostUsername)}`);
  };

  const handleChat = () => {
    if (!hostTelegramUsername) {
      toast.error('Seller has not linked Telegram');
      return;
    }
    haptic('medium');
    const tgUrl = `https://t.me/${hostTelegramUsername}`;
    const tgApp = tg();
    if (tgApp?.openTelegramLink) {
      try { tgApp.openTelegramLink(tgUrl); return; } catch { /* fall through */ }
    }
    if (typeof window !== 'undefined') window.open(tgUrl, '_blank');
  };

  const handleBlock = async () => {
    if (!hostUsername) return;
    const msg = `Block @${hostUsername}? You won't see their listings or be able to bid on them.`;
    const tgApp = tg();
    let confirmed;
    if (tgApp?.showConfirm) {
      confirmed = await new Promise((resolve) => {
        try { tgApp.showConfirm(msg, (ok) => resolve(!!ok)); }
        catch { resolve(window.confirm(msg)); }
      });
    } else {
      confirmed = window.confirm(msg);
    }
    if (!confirmed) return;
    try {
      await axios.post(`${API}/users/block`, { target_username: hostUsername });
      toast.success(`Blocked @${hostUsername}`);
      onBlocked && onBlocked(hostUsername);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to block');
    }
  };

  return (
    <div data-testid="listing-actions" className={`flex items-center gap-2 ${className}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="listing-share-btn"
            type="button"
            className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-xl bg-indigo-500/15 hover:bg-indigo-500/25 active:scale-[0.98] border border-indigo-500/30 text-indigo-200 text-xs font-semibold transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-slate-900 border-slate-700 text-white min-w-[180px]">
          <DropdownMenuItem
            data-testid="share-twitter"
            onSelect={(e) => { e.preventDefault(); handleShareTo('twitter'); }}
            className="cursor-pointer focus:bg-blue-500/10 focus:text-blue-200"
          >
            <Twitter className="w-3.5 h-3.5 mr-2 text-blue-400" /> Post to Twitter / X
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="share-telegram"
            onSelect={(e) => { e.preventDefault(); handleShareTo('telegram'); }}
            className="cursor-pointer focus:bg-cyan-500/10 focus:text-cyan-200"
          >
            <SendIcon className="w-3.5 h-3.5 mr-2 text-cyan-400" /> Share on Telegram
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="share-copy"
            onSelect={(e) => { e.preventDefault(); handleShareTo('copy'); }}
            className="cursor-pointer focus:bg-slate-700/40"
          >
            <Copy className="w-3.5 h-3.5 mr-2 text-slate-400" /> Copy link
          </DropdownMenuItem>
          {typeof navigator !== 'undefined' && navigator.share && (
            <DropdownMenuItem
              data-testid="share-native"
              onSelect={(e) => { e.preventDefault(); handleShareTo('native'); }}
              className="cursor-pointer focus:bg-emerald-500/10 focus:text-emerald-200"
            >
              <Share2 className="w-3.5 h-3.5 mr-2 text-emerald-400" /> More…
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {hostUsername && (
        <button
          data-testid="listing-profile-btn"
          type="button"
          onClick={handleProfile}
          className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-xl bg-slate-700/40 hover:bg-slate-700/60 active:scale-[0.98] border border-slate-600/40 text-slate-200 text-xs font-semibold transition-colors"
        >
          <User className="w-3.5 h-3.5" /> Profile
        </button>
      )}
      {!isOwnListing && hostTelegramUsername && (
        <button
          data-testid="listing-chat-btn"
          type="button"
          onClick={handleChat}
          className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 active:scale-[0.98] border border-emerald-500/30 text-emerald-200 text-xs font-semibold transition-colors"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Chat
        </button>
      )}
      {!isOwnListing && (targetId || hostUsername) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="listing-overflow-btn"
              type="button"
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-800/40 hover:bg-slate-800/60 active:scale-[0.98] border border-slate-700/40 text-slate-300"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-slate-900 border-slate-700 text-white min-w-[140px]">
            {targetId && targetType && (
              <DropdownMenuItem
                data-testid="listing-report-action"
                onSelect={(e) => { e.preventDefault(); setShowReport(true); }}
                className="text-red-300 focus:bg-red-500/10 focus:text-red-200 cursor-pointer"
              >
                <Flag className="w-3.5 h-3.5 mr-2" /> Report
              </DropdownMenuItem>
            )}
            {hostUsername && (
              <DropdownMenuItem
                data-testid="listing-block-action"
                onSelect={(e) => { e.preventDefault(); handleBlock(); }}
                className="text-amber-300 focus:bg-amber-500/10 focus:text-amber-200 cursor-pointer"
              >
                <Ban className="w-3.5 h-3.5 mr-2" /> Block seller
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ReportDialog
        open={showReport}
        onOpenChange={setShowReport}
        targetType={targetType}
        targetId={targetId}
        targetName={targetName}
      />
    </div>
  );
}

/**
 * Compact metadata row (location · time-ago · category · NEW).
 * Used at the bottom of listing detail pages.
 *
 * Props: createdAt, location, prizeType, orderNumber (optional - shown if completed)
 */
export function ListingMeta({ createdAt, location, prizeType, orderNumber, className = '' }) {
  const showNew = isNewListing(createdAt);
  const category = PRETTY_CATEGORY[prizeType] || prizeType;
  const ago = timeAgo(createdAt);

  if (!createdAt && !location && !prizeType && !orderNumber) return null;

  return (
    <div data-testid="listing-meta" className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px] text-slate-500 ${className}`}>
      {showNew && (
        <span data-testid="listing-meta-new" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-bold uppercase tracking-wider">
          <Sparkles className="w-2.5 h-2.5" /> New
        </span>
      )}
      {category && (
        <span data-testid="listing-meta-category" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-700/40 border border-slate-600/40 text-slate-300 font-medium">
          <Tag className="w-2.5 h-2.5" /> {category}
        </span>
      )}
      {ago && (
        <span data-testid="listing-meta-ago" className="inline-flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" /> {ago}
        </span>
      )}
      {location && (
        <span data-testid="listing-meta-location" className="inline-flex items-center gap-1">
          <MapPin className="w-2.5 h-2.5" /> {location}
        </span>
      )}
      {orderNumber && (
        <span data-testid="listing-meta-order" className="inline-flex items-center gap-1 font-mono text-slate-400 ml-auto">
          {orderNumber}
        </span>
      )}
    </div>
  );
}

export default ListingActions;
